// Données structurées (JSON-LD) : établissement + appartements.
// Règle : aucune donnée inventée — un champ vide n'est jamais émis.
import { SITE_URL, has, EQUIP_CATEGORIES } from "@/lib/api";

export const BRAND = "Le Chalet Badouille";
export const ESTABLISHMENT = "Le Chalet Badouille Izoard";
export const BUSINESS_ID = `${SITE_URL}/#etablissement`;
export const TOTAL_CAPACITY = 18;
export const UNITS_COUNT = 3;
// Image de partage par défaut (logo sur fond clair, 1200x630)
export const DEFAULT_OG_IMAGE = "/og-badouille.jpg";

export const absUrl = (u) => (!has(u) ? undefined : /^https?:\/\//i.test(u) ? u : `${SITE_URL}${u.startsWith("/") ? "" : "/"}${u}`);
const clean = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => has(v) && !(typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0)));
const uniq = (arr) => [...new Set(arr.filter(has))];

export function postalAddress(settings = {}) {
  const line2 = settings.address_line2 || "05350 Arvieux";
  const m = /^(\d{5})\s+(.+)$/.exec(line2.trim());
  return clean({
    "@type": "PostalAddress",
    streetAddress: settings.address_line1 || "113 rue du Campanil",
    postalCode: m ? m[1] : "05350",
    addressLocality: m ? m[2] : "Arvieux",
    addressRegion: "Hautes-Alpes",
    addressCountry: "FR",
  });
}

const apartmentUrl = (a) => `${SITE_URL}/appartements/${a.slug}`;

function accommodationSummary(a) {
  return clean({
    "@type": "Apartment",
    "@id": `${apartmentUrl(a)}#logement`,
    name: a.name,
    url: apartmentUrl(a),
    occupancy: has(a.capacity) ? { "@type": "QuantitativeValue", maxValue: a.capacity } : undefined,
    numberOfBedrooms: a.bedrooms_count,
  });
}

export function businessJsonLd({ settings = {}, content = {}, apartments = [], description } = {}) {
  const published = apartments.filter((a) => a && a.slug);
  const capacity = published.reduce((s, a) => s + (Number(a.capacity) || 0), 0);
  const social = settings.social || {};
  return clean({
    "@context": "https://schema.org",
    "@type": ["VacationRental", "LodgingBusiness"],
    "@id": BUSINESS_ID,
    name: settings.site_name || ESTABLISHMENT,
    alternateName: BRAND,
    url: SITE_URL,
    description: description || content.footer?.text,
    image: uniq([content.hero?.image_url, content.hero?.image_summer_url, content.hero?.image_winter_url, content.welcome?.image_url].map(absUrl)),
    logo: absUrl(settings.logo_url),
    address: postalAddress(settings),
    telephone: settings.phone,
    email: settings.email,
    maximumAttendeeCapacity: capacity || TOTAL_CAPACITY,
    numberOfRooms: published.length || UNITS_COUNT,
    containsPlace: published.map(accommodationSummary),
    sameAs: uniq([social.facebook, social.instagram]),
  });
}

export function apartmentJsonLd(apt, settings = {}) {
  if (!apt) return undefined;
  const amenities = EQUIP_CATEGORIES.flatMap((c) => apt.equipments?.[c] || [])
    .filter(has).map((name) => ({ "@type": "LocationFeatureSpecification", name, value: true }));
  const images = uniq([apt.main_image, ...(apt.images || []).map((i) => i.url)].map(absUrl));
  return {
    "@context": "https://schema.org",
    "@graph": [
      clean({
        ...accommodationSummary(apt),
        description: apt.short_description,
        image: images,
        numberOfRooms: apt.bedrooms_count,
        floorSize: has(apt.surface) ? { "@type": "QuantitativeValue", value: apt.surface, unitCode: "MTK" } : undefined,
        amenityFeature: amenities,
        address: postalAddress(settings),
        containedInPlace: { "@type": ["VacationRental", "LodgingBusiness"], "@id": BUSINESS_ID, name: settings.site_name || ESTABLISHMENT, url: SITE_URL, address: postalAddress(settings) },
      }),
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Nos appartements", item: `${SITE_URL}/appartements` },
          { "@type": "ListItem", position: 3, name: apt.name, item: apartmentUrl(apt) },
        ],
      },
    ],
  };
}
